/**
 * Libellés et tons d'Hermès.
 *
 * Volontairement distinct de `src/ui/lib/labels.ts` : les deux interfaces
 * n'ont pas la même palette, donc pas les mêmes classes de pastille. Ce
 * fichier ne duplique **que** la table des libellés et le choix des tons —
 * la logique métier reste dans `src/lib/`, partagée, jamais recopiée.
 *
 * C'est ce cloisonnement qui permet de supprimer `src/hermes/` ou `src/ui/`
 * sans toucher à l'autre.
 *
 * Plus court que son jumeau, et c'est voulu : cette interface ne fait plus
 * saisir de statut commercial. Il se déduit des gestes — garder, fixer un
 * rendez-vous, annuler — donc ni la table des statuts, ni leurs tons, ni la
 * liste proposée à la saisie n'ont de raison d'exister ici.
 */

import type {
  LibraryStatus,
  ProspectPriority,
  WebsiteQuality,
  WebsiteType,
} from '../../types';
import type { OpenStatus } from '../../lib/openingHours';

/** Non exporté : seul `websiteLabel` s'en sert, juste en dessous. */
const WEBSITE_TYPE_SHORT_LABELS: Record<WebsiteType, string> = {
  NONE: 'Aucun site',
  SOCIAL: 'Réseau social',
  PLATFORM: 'Plateforme',
  OWN_WEBSITE: 'Site propre',
};

export const WEBSITE_QUALITY_LABELS: Record<WebsiteQuality, string> = {
  UNKNOWN: 'À évaluer',
  GOOD: 'Bon',
  BAD: 'Mauvais',
};

export const PRIORITY_LABELS: Record<ProspectPriority, string> = {
  HIGH: 'Prioritaire',
  MEDIUM: 'Intéressant',
  LOW: 'Faible priorité',
};

export const LIBRARY_STATUS_LABELS: Record<LibraryStatus, string> = {
  UNSAVED: 'Non enregistré',
  SAVED: 'En bibliothèque',
  IGNORED: 'Ignoré',
  TREATED: 'Déjà traité',
  CONFIRMED: 'Rendez-vous fixé',
};

/**
 * Le ton d'un état de présence web, sur la rampe chaude.
 *
 * L'ordre encode la **vendabilité**, pas la qualité : mauvais site confirmé,
 * absence de site et page de réseau social pèsent désormais le même score et
 * partagent donc la teinte la plus chaude. Un bon site retombe au neutre — il
 * n'y a rien à vendre.
 */
export function websiteTone(type: WebsiteType, quality: WebsiteQuality = 'UNKNOWN'): string {
  if (type === 'OWN_WEBSITE') {
    if (quality === 'BAD') return 'badge-opp-4';
    if (quality === 'GOOD') return 'badge-opp-0';
    return 'badge-opp-1';
  }
  if (type === 'NONE' || type === 'SOCIAL') return 'badge-opp-4';
  return 'badge-opp-1';
}

export function websiteLabel(type: WebsiteType, quality: WebsiteQuality): string {
  if (type === 'OWN_WEBSITE' && quality !== 'UNKNOWN') {
    return `Site ${WEBSITE_QUALITY_LABELS[quality].toLowerCase()}`;
  }
  return WEBSITE_TYPE_SHORT_LABELS[type];
}

/**
 * L'état d'un établissement vis-à-vis de la bibliothèque, dans les résultats.
 *
 * Ce n'est pas une opportunité : il ne prend jamais la rampe chaude. « Déjà
 * traité » est un état de mémoire et se distingue par la forme, sans consommer
 * de teinte.
 */
export function libraryStatusTone(status: LibraryStatus): string {
  if (status === 'CONFIRMED') return 'badge-go';
  if (status === 'TREATED') return 'badge-memo';
  if (status === 'SAVED') return 'badge-brand';
  return 'badge-opp-0';
}

/**
 * Libellé d'horaire, accentué.
 *
 * Le **calcul** reste partagé — `getOpenStatus` est la vraie logique, testée,
 * et n'est pas dupliquée ici. Seul le libellé est réécrit.
 */
export function openStatusLabel(status: OpenStatus): string {
  if (status.state === 'UNKNOWN') return 'Horaires inconnus';
  if (status.alwaysOpen) return 'Ouvert 24 h/24';
  if (status.state === 'OPEN') {
    return status.closesAt === null ? 'Ouvert' : `Ouvert — ferme à ${status.closesAt}`;
  }
  return status.opensAt === null ? 'Fermé' : `Fermé — ouvre à ${status.opensAt}`;
}
